// Cloudflare Turnstile — invisible-friendly anti-spam widget for the contact
// forms. It only appears when an admin has enabled it and entered a site key in
// Site settings; otherwise nothing renders and forms behave exactly as before
// (so the design is unchanged until you switch it on).
import { useCallback, useEffect, useRef, useState } from "react";
import { useBlock } from "@/lib/content";

let _scriptPromise = null;
function loadTurnstileScript() {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// Returns { Widget, token, required, reset }. Forms render {Widget} above their
// submit button, block submit when `required && !token`, send `turnstileToken`,
// and call reset() after a successful submission.
export function useTurnstile() {
  const settings = useBlock("settings", null);
  const ts = (settings && settings.security && settings.security.turnstile) || {};
  const siteKey = ts.siteKey || "";
  const enabled = !!ts.enabled && !!siteKey;

  const [token, setToken] = useState("");
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !ref.current) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (t) => setToken(t),
          "error-callback": () => setToken(""),
          "expired-callback": () => setToken(""),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try {
        if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      } catch {
        /* ignore */
      }
      widgetId.current = null;
    };
  }, [enabled, siteKey]);

  const reset = useCallback(() => {
    setToken("");
    try {
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
    } catch {
      /* ignore */
    }
  }, []);

  const Widget = enabled ? <div ref={ref} className="mt-3" data-testid="turnstile" /> : null;
  return { Widget, token, required: enabled, reset };
}
